import type { PatternType } from '@miyuki/types';
import { cn } from '@miyuki/ui';

import { t } from '@/lib/i18n';

/**
 * Elección del tejido sobre el que se diseña.
 *
 * Cada opción lleva su nombre **y** un diagrama de su disposición: quien nunca
 * oyó «peyote» ni «loom» tiene que poder elegir igual, mirando la forma
 * (RF-02). El diagrama es decorativo; lo que se anuncia es el nombre.
 */
export interface PatternTypePickerProps {
  readonly id: string;
  readonly value: PatternType;
  readonly onChange: (value: PatternType) => void;
}

const OPTIONS: readonly {
  value: PatternType;
  label: () => string;
  hint: () => string;
}[] = [
  {
    value: 'loom',
    label: () => t('patternTypeLoom'),
    hint: () => t('patternTypeLoomHint'),
  },
  {
    value: 'peyote',
    label: () => t('patternTypePeyote'),
    hint: () => t('patternTypePeyoteHint'),
  },
];

/** Tres filas de cuadros, rectas o corridas media celda. */
function TypeDiagram({ staggered }: { readonly staggered: boolean }) {
  return (
    <span aria-hidden="true" className="flex flex-col gap-0.5">
      {[0, 1, 2].map((row) => (
        <span
          key={row}
          className="flex gap-0.5"
          style={{ marginLeft: staggered && row % 2 === 1 ? 5 : 0 }}
        >
          {[0, 1, 2, 3].map((column) => (
            <span key={column} className="bg-foreground/35 size-2.5 rounded-[2px]" />
          ))}
        </span>
      ))}
    </span>
  );
}

export function PatternTypePicker({ id, value, onChange }: PatternTypePickerProps) {
  return (
    <div id={id} role="radiogroup" aria-label={t('patternType')} className="flex gap-2">
      {OPTIONS.map((option) => {
        const active = option.value === value;

        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            className={cn(
              'border-border flex min-h-11 flex-1 flex-col items-start gap-2 rounded-lg border p-3 text-left',
              active && 'border-primary ring-primary/40 ring-2',
            )}
            onClick={() => {
              onChange(option.value);
            }}
          >
            <TypeDiagram staggered={option.value === 'peyote'} />
            <span className="text-sm font-medium">{option.label()}</span>
            <span className="text-muted-foreground text-xs">{option.hint()}</span>
          </button>
        );
      })}
    </div>
  );
}
